import React, { useEffect, useState } from "react";
import classnames from "classnames";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import { getDeals } from "api/requests/getDeals";
import { Action, Category, Label } from "constants/events";
import { Coordinates } from "custom-types/Coordinates";
import { Deal } from "custom-types/Deals";
import useDomain from "hooks/useDomain";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useSearch from "redux/hooks/useSearch";
import {
  DEFAULT_SEARCH_SKIP,
  DEFAULT_SEARCH_TAKE,
} from "redux/reducers/search";
import { getCoordinates } from "redux/selectors/location";
import { getRoundedDistance } from "utils/distance";

import ArticleResults from "components/ArticleResults";
import BrandResults from "components/BrandResults";
import DispensaryResults from "components/DispensaryResults";
import MenuItemResults from "components/MenuItemResults";
import SearchAd from "components/SearchAd";
import DesktopSearchAdPlaceholder from "components/SearchAd/DesktopSearchAdPlaceholder";
import MobileSearchAdPlaceholder from "components/SearchAd/MobileSearchAdPlaceholder";
import SearchFilter from "components/SearchFilter";
import SearchFiltersPlaceholder from "components/SearchFilter/SearchFiltersPlaceholder";
import DealsCarousel from "components/Shared/DealsCarousel";
import RecentlyViewedMenuItems from "components/Shared/RecentlyViewedMenuItems";
import SearchResultsPlaceholder from "components/Shared/Skeletons/SearchResults/SearchResultsPlaceholder";
import StrainResults from "components/StrainResults";

export const EXCLUDE_TYPES = ["product"];
const DEALS_TAKE = 8;
const DEALS_SKIP = 0;

const SearchPage: NextPage = () => {
  const [dealsData, setDealsData] = useState<Deal[]>([]);
  const coordinates = useSelector(getCoordinates);
  const countryCode = useDomainCountryCode();
  const {
    loading,
    searchTerm,
    categoryRelevanceOrder,
    brand,
    dispensary,
    strain,
    nullResults,
    getSearchResults,
  } = useSearch();
  const { publishEvent } = useEventTracker();
  const { asPath, query } = useRouter();
  const baseUrl = useDomain();

  React.useEffect(() => {
    if (nullResults) {
      publishEvent({
        action: Action.impression,
        category: Category.searchResults,
        label: `${Label.nullResults}_${searchTerm}`,
        nonInteractionEvent: true,
      });
    }
  }, [nullResults]);

  const resultingCategories = query.searchCategory
    ? Array.isArray(query.searchCategory)
      ? query.searchCategory
      : [query.searchCategory]
    : categoryRelevanceOrder;

  const getCategoryComponent = (category: string) => {
    switch (category) {
      case "article":
        return <ArticleResults />;
      case "brand":
        return <BrandResults />;
      case "dispensary":
        return <DispensaryResults />;
      case "menuItem":
        return <MenuItemResults deals={dealsData} />;
      case "strain":
        return <StrainResults />;

      default:
        return <></>;
    }
  };

  const topCategory = resultingCategories[0];
  let topResult: string | undefined = undefined;
  if (topCategory === "brand") {
    topResult = brand[0]?.name;
  }
  if (topCategory === "dispensary") {
    topResult = dispensary[0]?.name;
  }
  if (topCategory === "strain") {
    topResult = strain[0]?.name;
  }
  const adTargeting = { category: topCategory, result: topResult };

  const fetchDeals = async (coordinates: Coordinates, countryCode: string) => {
    const { deals } = await getDeals(
      coordinates,
      countryCode,
      DEALS_SKIP,
      DEALS_TAKE,
    );

    setDealsData(deals);
  };

  useEffect(() => {
    fetchDeals(coordinates, countryCode);
  }, [coordinates]);

  const fetchSearchResults = async () => {
    await getSearchResults(
      DEFAULT_SEARCH_TAKE,
      DEFAULT_SEARCH_SKIP,
      undefined,
      true,
    );
  };

  useEffect(() => {
    fetchSearchResults();
  }, []);

  return (
    <>
      <Head>
        <meta property="og:url" content={`${baseUrl}${asPath}`} key="og:url" />
        <title>Search | Leafly</title>
        <meta name="robots" content="noindex,noarchive,nofollow" />
      </Head>
      <section role="contentinfo" className="container pt-lg">
        {searchTerm && (
          <h1 className="heading--m mb-xl mt-sm">{`Search results for "${searchTerm}"`}</h1>
        )}
        {loading ? (
          <SearchFiltersPlaceholder />
        ) : (
          <>
            {searchTerm && nullResults && (
              <p className="mb-section" data-testid="null-content">
                Oh no! Your search for <strong>“{searchTerm}”</strong> has come
                up empty! Try a new search.
              </p>
            )}
            {!nullResults && (
              <SearchFilter sortedCategories={categoryRelevanceOrder} />
            )}
          </>
        )}
      </section>

      <div className="bg-leafly-white pt-lg">
        <div className="container">
          {loading ? (
            <>
              <div className="md:hidden">
                <MobileSearchAdPlaceholder />
              </div>
              <div className="hidden md:block">
                <DesktopSearchAdPlaceholder />
              </div>
            </>
          ) : (
            <SearchAd tag="upper" customTargeting={adTargeting} />
          )}
        </div>
      </div>
      <section>
        {loading ? (
          <section className="results-section">
            <SearchResultsPlaceholder />
          </section>
        ) : (
          <>
            {resultingCategories.map((category: string, index: number) => (
              <section
                key={category}
                className={classnames("pb-section results-section", {
                  "pt-lg": index === 0,
                  "pt-section": index > 0,
                })}
                data-testid={`results-section-${category}`}
              >
                <div className="container">
                  <div>{getCategoryComponent(category)}</div>
                </div>
              </section>
            ))}
          </>
        )}
        <section className="container py-section">
          {nullResults && <DealsCarousel deals={dealsData} />}
          <RecentlyViewedMenuItems
            className="my-section"
            onCardImpression={(menuItem) => {
              publishEvent({
                action: Action.impression,
                category: Category.recentlyViewedMenuItems,
                dispensaryDistance: getRoundedDistance(
                  menuItem.dispensary.distanceMi,
                  countryCode,
                ),
                dispensaryId: menuItem.dispensaryId,
              });
            }}
          />
          <SearchAd tag="lower" customTargeting={adTargeting} />
        </section>
      </section>

      <style jsx>
        {`
          .results-section {
            background: linear-gradient(
              180deg,
              var(--color-leafly-white) 0%,
              var(--color-white) 100%
            );
          }
        `}
      </style>
    </>
  );
};

export default SearchPage;
